import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight, ArrowLeft, Tag, Truck, FileText, ShieldCheck, Loader2, CheckCircle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import BackButton from "@/components/BackButton";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export const Cart = () => {
  const { cart, loading, updateQuantity, removeFromCart, clearCart } = useCart();
  const { formatPrice } = useCurrency();
  const [couponCode, setCouponCode] = useState("");
  const [couponApplied, setCouponApplied] = useState(null); // { code, discount_percent, discount_amount }
  const [couponError, setCouponError] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [shippingCountry, setShippingCountry] = useState("GB");
  const [shippingPostcode, setShippingPostcode] = useState("");
  const [estimatedShipping, setEstimatedShipping] = useState({ method: "Royal Mail Tracked", cost: 3.99, details: "Delivery within 2-4 working days" });
  const [orderInstructions, setOrderInstructions] = useState("");
  const [continueShoppingUrl, setContinueShoppingUrl] = useState("/products");

  // Get the last shopping category for continue shopping link
  useEffect(() => {
    const lastCategory = localStorage.getItem("last_shopping_category");
    if (lastCategory) {
      setContinueShoppingUrl(`/products/${lastCategory}`);
    }
  }, []);

  // Auto-calculate shipping when country changes
  useEffect(() => {
    if (shippingCountry === "GB") {
      setEstimatedShipping({ 
        method: "Royal Mail Tracked", 
        cost: 3.99,
        details: "Delivery within 2-4 working days"
      });
    } else if (shippingCountry === "US") {
      setEstimatedShipping({ 
        method: "International Tracked - USA", 
        cost: 19.99,
        details: "Delivery within 7-14 working days. You may be liable for local import duties/taxes."
      });
    } else if (shippingCountry === "AU") {
      setEstimatedShipping({ 
        method: "International Tracked - Australia", 
        cost: 19.99,
        details: "Delivery within 7-14 working days. You may be liable for local import duties/taxes."
      });
    } else {
      // Rest of World: £9.99
      setEstimatedShipping({ 
        method: "International Tracked", 
        cost: 9.99,
        details: "Delivery within 7-14 working days. You may be liable for local import duties/taxes."
      });
    }
  }, [shippingCountry]);

  const handleApplyCoupon = async () => {
    setCouponError("");
    if (!couponCode.trim()) {
      setCouponError("Please enter a coupon code");
      return;
    }
    
    setCouponLoading(true);
    try {
      const response = await axios.post(`${API}/api/discounts/validate`, {
        code: couponCode.trim().toUpperCase(),
        order_total: cart.total
      });
      
      setCouponApplied({
        code: response.data.code,
        discount_percent: response.data.discount_percent,
        discount_amount: response.data.discount_amount
      });
      setCouponError("");
      // Store in localStorage to persist to checkout
      localStorage.setItem("applied_discount", JSON.stringify({
        code: response.data.code,
        discount_percent: response.data.discount_percent,
        discount_amount: response.data.discount_amount
      }));
    } catch (error) {
      setCouponError(error.response?.data?.detail || "Invalid discount code");
      setCouponApplied(null);
      localStorage.removeItem("applied_discount");
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponApplied(null);
    setCouponCode("");
    setCouponError("");
    localStorage.removeItem("applied_discount");
  };

  // Check if country is UK (VAT applies) or international (VAT exempt)
  const isUKOrder = shippingCountry === "GB";
  const vatRate = isUKOrder ? 0.2 : 0;
  
  // Calculate totals with discount
  const subtotalBeforeDiscount = cart.total;
  const discountAmount = couponApplied?.discount_amount || 0;
  const subtotalAfterDiscount = subtotalBeforeDiscount - discountAmount;
  const shippingCost = estimatedShipping?.cost || 0;
  const vatAmount = (subtotalAfterDiscount + shippingCost) * vatRate;
  const totalWithVat = subtotalAfterDiscount + shippingCost + vatAmount;

  const handleEstimateShipping = () => {
    // Shipping costs - automatically calculate based on country selection
    // UK: £3.99, USA: £19.99, Australia: £19.99, Rest of World: £9.99
    
    if (shippingCountry === "GB") {
      setEstimatedShipping({ 
        method: "Royal Mail Tracked", 
        cost: 3.99,
        details: "Delivery within 2-4 working days"
      });
    } else if (shippingCountry === "US") {
      setEstimatedShipping({ 
        method: "International Tracked - USA", 
        cost: 19.99,
        details: "Delivery within 7-14 working days. You may be liable for local import duties/taxes."
      });
    } else if (shippingCountry === "AU") {
      setEstimatedShipping({ 
        method: "International Tracked - Australia", 
        cost: 19.99,
        details: "Delivery within 7-14 working days. You may be liable for local import duties/taxes."
      });
    } else {
      // Rest of World: £9.99
      setEstimatedShipping({ 
        method: "International Tracked", 
        cost: 9.99,
        details: "Delivery within 7-14 working days. You may be liable for local import duties/taxes."
      });
    }
  };

  if (loading) {
    return (
      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="space-y-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex gap-6 animate-pulse">
                <div className="w-24 h-24 bg-secondary" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-secondary w-1/2" />
                  <div className="h-4 bg-secondary w-1/4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (cart.items.length === 0) {
    return (
      <div className="pt-24 pb-16" data-testid="cart-empty">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center py-16">
            <ShoppingBag className="h-16 w-16 mx-auto mb-6 text-muted-foreground" strokeWidth={1} />
            <h1 className="text-2xl font-heading font-bold mb-4">Your cart is empty</h1>
            <p className="text-muted-foreground mb-8">
              Looks like you have not added any items yet.
            </p>
            <Link to={continueShoppingUrl}>
              <Button className="rounded-full uppercase tracking-widest" data-testid="continue-shopping">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Continue Shopping
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-16" data-testid="cart-page">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        {/* Back Button */}
        <div className="mb-4">
          <BackButton label="Continue Shopping" />
        </div>
        
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="label-style mb-2">Your</p>
            <h1 className="section-title">Cart</h1>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1 text-green-600">
              <ShieldCheck className="h-5 w-5" />
              <span className="text-xs font-medium">SSL Secure</span>
            </div>
            <button
              onClick={clearCart}
              className="text-sm text-muted-foreground hover:text-destructive transition-colors"
              data-testid="clear-cart-btn"
            >
              Clear Cart
            </button>
          </div>
        </div>

        {/* Shipping Time Notice */}
        <div className="flex items-center gap-3 p-4 mb-6 bg-blue-50 border border-blue-200 rounded-lg" data-testid="shipping-notice">
          <Truck className="h-5 w-5 text-blue-600 flex-shrink-0" />
          <p className="text-sm text-blue-800 font-medium">
            Current shipping time - 3-5 working days.
          </p>
        </div>

        <div className="space-y-6">
          {cart.items.map((item, index) => (
            <div
              key={`${item.product_id}-${item.selected_color || ''}-${index}`}
              className="flex gap-6 border-b border-border pb-6"
              data-testid={`cart-item-${item.product_id}`}
            >
              <Link to={`/product/${item.product_id}`} className="shrink-0">
                {item.image_url ? (
                  <img 
                    src={item.image_url} 
                    alt={item.name}
                    className="w-24 h-24 object-cover bg-gray-100"
                  />
                ) : (
                  <div className="w-24 h-24 bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-500 text-xs text-center px-1">Awaiting Image</span>
                  </div>
                )}
              </Link>
              <div className="flex-1 min-w-0">
                <Link to={`/product/${item.product_id}`}>
                  <h3 className="font-bold hover:text-accent transition-colors truncate">
                    {item.name}
                  </h3>
                </Link>
                <p className="price-font text-muted-foreground mt-1">
                  {item.bulk_discount_applied ? (
                    <span className="flex items-center gap-2">
                      <span className="line-through text-gray-400">{formatPrice(item.original_price)}</span>
                      <span className="text-green-600 font-semibold">{formatPrice(item.price)}</span>
                    </span>
                  ) : (
                    formatPrice(item.price)
                  )}
                </p>
                
                {/* Bulk Discount Badge for Wrap Vinyl */}
                {item.bulk_discount_applied && (
                  <div className="flex items-center gap-1 mt-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium w-fit" data-testid="bulk-discount-badge">
                    <Tag className="h-3 w-3" />
                    Bulk Discount Applied (25+ units)
                  </div>
                )}
                
                {/* Wrap Vinyl - Hint for bulk discount if close to threshold */}
                {item.is_wrap_vinyl && !item.bulk_discount_applied && item.quantity >= 20 && item.quantity < 25 && (
                  <p className="text-xs text-orange-600 mt-1">
                    Add {25 - item.quantity} more for bulk discount (£26.75 each)
                  </p>
                )}
                
                {/* Color Selection Display */}
                {item.selected_color && (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs text-muted-foreground uppercase tracking-wider">Colour:</span>
                    <span className="text-sm font-medium">{item.selected_color}</span>
                  </div>
                )}
                
                {/* Instructions Display */}
                {item.instructions && (
                  <div className="mt-2">
                    <span className="text-xs text-muted-foreground uppercase tracking-wider">Instructions:</span>
                    <p className="text-sm mt-1 text-muted-foreground italic bg-secondary/50 p-2 rounded">
                      {item.instructions}
                    </p>
                  </div>
                )}
                
                <div className="flex items-center gap-4 mt-4">
                  <div className="flex items-center border border-input">
                    <button
                      onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                      className="p-2 hover:bg-secondary transition-colors"
                      data-testid={`decrease-${item.product_id}`}
                    >
                      <Minus className="h-3 w-3" />
                    </button>
                    <span className="w-8 text-center text-sm">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                      className="p-2 hover:bg-secondary transition-colors"
                      data-testid={`increase-${item.product_id}`}
                    >
                      <Plus className="h-3 w-3" />
                    </button>
                  </div>
                  <button
                    onClick={() => removeFromCart(item.product_id)}
                    className="text-muted-foreground hover:text-destructive transition-colors"
                    data-testid={`remove-${item.product_id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="text-right">
                <p className="price-font font-bold">
                  {formatPrice(item.item_total)}
                </p>
                {item.bulk_discount_applied && item.original_price && (
                  <p className="text-xs text-green-600 mt-1">
                    Saving {formatPrice((item.original_price - item.price) * item.quantity)}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Cart Summary - 3 Column Layout */}
        <div className="mt-10 border-t border-border pt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Discount Code Section - Left */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Tag className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Discount Code</span>
              </div>
              {!couponApplied ? (
                <>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      placeholder="Enter coupon code"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      className="flex-1"
                      data-testid="coupon-input"
                      disabled={couponLoading}
                    />
                    <Button 
                      variant="outline" 
                      onClick={handleApplyCoupon}
                      className="rounded-full"
                      data-testid="apply-coupon-btn"
                      disabled={couponLoading}
                    >
                      {couponLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
                    </Button>
                  </div>
                  {couponError && (
                    <p className="text-sm text-destructive mt-2">{couponError}</p>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <div>
                      <p className="text-sm font-medium text-green-800">{couponApplied.code}</p>
                      <p className="text-xs text-green-600">{couponApplied.discount_percent}% off - Saving {formatPrice(couponApplied.discount_amount)}</p>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleRemoveCoupon}
                    className="h-8 w-8 p-0 hover:bg-green-100"
                  >
                    <X className="h-4 w-4 text-green-700" />
                  </Button>
                </div>
              )}
            </div>

            {/* Estimate Shipping Section - Middle */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Truck className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Shipping Destination</span>
              </div>
              <div className="space-y-3">
                <select
                  value={shippingCountry}
                  onChange={(e) => setShippingCountry(e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-md text-sm bg-white"
                  data-testid="shipping-country"
                >
                  <optgroup label="United Kingdom">
                    <option value="GB">United Kingdom - £3.99</option>
                  </optgroup>
                  <optgroup label="Americas">
                    <option value="US">United States - £19.99</option>
                    <option value="CA">Canada - £9.99</option>
                    <option value="MX">Mexico - £9.99</option>
                    <option value="BR">Brazil - £9.99</option>
                  </optgroup>
                  <optgroup label="Asia Pacific">
                    <option value="AU">Australia - £19.99</option>
                    <option value="NZ">New Zealand - £9.99</option>
                    <option value="JP">Japan - £9.99</option>
                    <option value="SG">Singapore - £9.99</option>
                    <option value="HK">Hong Kong - £9.99</option>
                  </optgroup>
                  <optgroup label="Europe">
                    <option value="IE">Ireland - £9.99</option>
                    <option value="DE">Germany - £9.99</option>
                    <option value="FR">France - £9.99</option>
                    <option value="ES">Spain - £9.99</option>
                    <option value="IT">Italy - £9.99</option>
                    <option value="NL">Netherlands - £9.99</option>
                    <option value="BE">Belgium - £9.99</option>
                    <option value="AT">Austria - £9.99</option>
                    <option value="PT">Portugal - £9.99</option>
                    <option value="PL">Poland - £9.99</option>
                    <option value="SE">Sweden - £9.99</option>
                    <option value="DK">Denmark - £9.99</option>
                    <option value="NO">Norway - £9.99</option>
                    <option value="CH">Switzerland - £9.99</option>
                  </optgroup>
                  <optgroup label="Rest of World">
                    <option value="AE">United Arab Emirates - £9.99</option>
                    <option value="SA">Saudi Arabia - £9.99</option>
                    <option value="ZA">South Africa - £9.99</option>
                    <option value="OTHER">Other Country - £9.99</option>
                  </optgroup>
                </select>
              </div>
              {estimatedShipping && (
                <div className="mt-3 p-3 bg-white rounded border border-green-200">
                  <div className="flex justify-between items-center">
                    <p className="text-sm font-medium text-green-700">
                      {estimatedShipping.method}
                    </p>
                    <p className="text-sm font-bold text-green-700">
                      {formatPrice(estimatedShipping.cost)}
                    </p>
                  </div>
                  {estimatedShipping.details && (
                    <p className="text-xs text-muted-foreground mt-2">
                      {estimatedShipping.details}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Order Summary - Right */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="price-font">{formatPrice(subtotalBeforeDiscount)}</span>
                </div>
                {couponApplied && (
                  <div className="flex justify-between items-center text-green-600">
                    <span className="text-sm flex items-center gap-1">
                      <Tag className="h-3 w-3" />
                      Discount ({couponApplied.discount_percent}%)
                    </span>
                    <span className="price-font">-{formatPrice(discountAmount)}</span>
                  </div>
                )}
                {estimatedShipping && (
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground text-sm">
                      Shipping
                    </span>
                    <span className="price-font">{formatPrice(estimatedShipping.cost)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">
                    {isUKOrder ? "VAT (20%)" : "VAT (Export - 0%)"}
                  </span>
                  <span className="price-font">{formatPrice(vatAmount)}</span>
                </div>
                {!isUKOrder && (
                  <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                    VAT exempt for international orders. You may be liable for local import duties/taxes upon delivery.
                  </p>
                )}
                <div className="flex justify-between items-center pt-3 border-t border-border">
                  <span className="text-lg font-medium">Total</span>
                  <span className="price-font text-2xl font-bold">
                    {formatPrice(totalWithVat)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Terms, Buttons Section */}
        <div className="mt-6">
          {/* Add Instructions to Order */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Add further Instruction to order</span>
            </div>
            <textarea
              value={orderInstructions}
              onChange={(e) => setOrderInstructions(e.target.value)}
              placeholder="Enter any special instructions for your order (e.g., custom text, specific requirements)..."
              className="w-full px-3 py-2 border border-input rounded-md text-sm bg-white min-h-[80px] resize-none"
              data-testid="order-instructions"
            />
          </div>

          {/* Terms and Conditions */}
          {!termsAccepted && (
            <p className="text-sm text-muted-foreground mb-2">
              Please accept the terms and conditions to proceed
            </p>
          )}
          <div className="flex items-start gap-2 mb-6">
            <Checkbox 
              id="terms" 
              checked={termsAccepted}
              onCheckedChange={setTermsAccepted}
              className="mt-0.5"
              data-testid="terms-checkbox"
            />
            <label htmlFor="terms" className="text-sm cursor-pointer">
              I agree to the{" "}
              <Link to="/terms-of-service" target="_blank" rel="noopener noreferrer" className="underline hover:no-underline">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/shipping-returns" target="_blank" rel="noopener noreferrer" className="underline hover:no-underline">
                Shipping & Returns Policy
              </Link>
            </label>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <Link to={continueShoppingUrl} className="flex-1">
              <Button variant="outline" className="w-full rounded-full uppercase tracking-widest">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Continue Shopping
              </Button>
            </Link>
            <Link to={termsAccepted ? "/checkout" : "#"} className="flex-1">
              <Button 
                className="w-full rounded-full uppercase tracking-widest"
                data-testid="checkout-btn"
                disabled={!termsAccepted}
                onClick={(e) => {
                  if (!termsAccepted) {
                    e.preventDefault();
                  } else {
                    // Store shipping estimate and order instructions in localStorage for checkout
                    if (estimatedShipping) {
                      localStorage.setItem('shipping_estimate', JSON.stringify({
                        ...estimatedShipping,
                        country: shippingCountry,
                        postcode: shippingPostcode,
                        isUKOrder: isUKOrder,
                        vatRate: vatRate
                      }));
                    } else {
                      // If no shipping estimate, still store country info for VAT calculation
                      localStorage.setItem('shipping_estimate', JSON.stringify({
                        method: null,
                        cost: 0,
                        country: shippingCountry,
                        postcode: shippingPostcode,
                        isUKOrder: isUKOrder,
                        vatRate: vatRate
                      }));
                    }
                    // Store order instructions
                    if (orderInstructions.trim()) {
                      localStorage.setItem('order_instructions', orderInstructions);
                    } else {
                      localStorage.removeItem('order_instructions');
                    }
                  }
                }}
              >
                Checkout
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          {/* SSL Secure and Payment Logos */}
          <div className="mt-8 pt-6 border-t border-border">
            <div className="flex flex-col items-center gap-4">
              <div className="flex items-center gap-2 text-green-600">
                <ShieldCheck className="h-6 w-6" />
                <span className="text-base font-semibold">SSL Secure Checkout</span>
              </div>
              <img 
                src="https://customer-assets.emergentagent.com/job_8ce91259-620f-4745-b8a0-a8b702a8c437/artifacts/e4fywrj8_Card%20Payment%20logos.png" 
                alt="Visa, Mastercard, American Express" 
                className="h-10"
              />
              <p className="text-xs text-muted-foreground">Payments securely processed by Stripe</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
